/** GERP PLM(123) — 레거시 appr_edit_forms_pdm_select_choice_mstr */
export const PRODUCT_LIFECYCLE_TYPE = 123;

export const isProductLifecycleType = (apvType: number): boolean =>
  apvType === PRODUCT_LIFECYCLE_TYPE;

export const PRODUCT_LIFECYCLE_MISSING_MESSAGE =
  '데이터를 로드하지 않았습니다. 조회 조건 선택 후 확인 버튼을 누르세요.';

export type PlmType = 'CENTRIC' | 'NEW_PLM';

export const PLM_TYPE_OPTIONS: { value: PlmType; label: string }[] = [
  { value: 'CENTRIC', label: 'PLM(CENTRIC)' },
  { value: 'NEW_PLM', label: 'NEW_PLM' },
];

export const PLM_PLACEHOLDERS: Record<PlmType, string> = {
  CENTRIC: 'PLM(CENTRIC) 품번을 선택하세요',
  NEW_PLM: 'NEW_PLM 품번을 선택하세요',
};

const isPlmType = (v: unknown): v is PlmType =>
  v === 'CENTRIC' || v === 'NEW_PLM';

type Value = string | number | null | undefined;

/** GERP 미상신 건 — 키는 GERP 응답 그대로 */
export type PlmCandidate = {
  styleId?: string | null;
  aprvId?: string | null;
  title?: string | null;
  brand?: string | null;
  yy?: string | null;
  season?: string | null;
  styleNo?: string | null;
  styleName?: string | null;
  pdfUrl?: string | null;
  plmType?: PlmType | null;
  dataPackageNo?: string | null;
};

/** GERP 본문 카드 1건 — 키는 GERP 응답 그대로 */
export type PlmCard = {
  plmType?: PlmType | null;
  aprvId?: Value;
  styleId?: Value;
  brand?: Value;
  brandName?: Value;
  yy?: Value;
  year?: Value;
  seasonRun?: Value;
  styleNo?: Value;
  styleName?: Value;
  familyStyleNo?: Value;
  sex?: Value;
  tempCost?: Value;
  typeName?: Value;
  clothesGroup?: Value;
  imageUrl?: Value;
  pdfUrl?: Value;
  dataPackageNo?: Value;
};

export type PlmDraftSource = {
  code: number;
  attributes: { plmType?: PlmType; styleNumber?: string } | unknown[];
  display: {
    candidates: PlmCandidate[] | null;
    selection: string[] | null;
    subject: string | null;
    content: { plmType?: PlmType; cards?: PlmCard[] | null } | null;
  };
};

/** 작성 중 고른 품번 1건 */
export type PlmItem = {
  /** draft-source itemIds 값 — CENTRIC 은 styleId, NEW_PLM 은 aprvId */
  key: string;
  styleNo: string;
  styleName: string;
  /** styleId+brand+year+styleNo+dataPackageNo */
  styleNumber: string;
  card: PlmCard;
};

/** 저장 attributes 와 같은 모양 */
export type ProductLifecycleState = {
  plmType: PlmType;
  styleNumber: string;
  /** 작성 시점 카드 HTML */
  content: string;
};

export const plmCandidateKey = (c: PlmCandidate, plmType: PlmType): string =>
  (plmType === 'CENTRIC' ? c.styleId : c.aprvId) ?? '';

const text = (v: unknown): string =>
  v === null || v === undefined ? '' : String(v).trim();

/** 부분 반려 itemId — CENTRIC 은 brand|year|styleNo, NEW_PLM 은 aprvId */
export const buildPlmItemId = (
  plmType: PlmType,
  v: { aprvId?: Value; brand?: Value; yy?: Value; styleNo?: Value },
): string =>
  plmType === 'NEW_PLM'
    ? text(v.aprvId)
    : [v.brand, v.yy, v.styleNo].map(text).join('|');

/** 레거시 add_stylefull — styleId+brand+yy+styleNo+dataPackageNo */
export const buildPlmStyleNumber = (c: PlmCandidate): string =>
  [c.styleId, c.brand, c.yy, c.styleNo, c.dataPackageNo].map(text).join('');

/** 레거시 change_title_content — 고른 순서의 첫 건 기준. 2건 이상은 1건 제목 뒤에 「외 N건」 */
export const buildPlmSubject = (
  items: PlmItem[],
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  if (!items.length) return '';
  const [first] = items;
  if (items.length === 1) return `PLM(${first.styleNo})-${first.styleName}`;
  return t('PLM({{styleNo}})-{{styleName}} 외 {{count}}건', {
    styleNo: first.styleNo,
    styleName: first.styleName,
    count: items.length - 1,
  });
};

/** 레거시 본문 — 고를 때마다 덮어씀 */
export const buildPlmEditorContent = (
  items: PlmItem[],
  t: (key: string) => string,
): string =>
  `<p>&nbsp;</p><p>${t('추가품번')} : ${items
    .map((item) => item.styleNo)
    .join(',')}&nbsp;</p><p>&nbsp;</p><p>&nbsp;</p>`;

const nonEmpty = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v : null;

export const parseProductLifecycleAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): ProductLifecycleState | null => {
  const content = nonEmpty(attributes?.content);
  if (!attributes || !content) return null;
  return {
    // 레거시 문서는 plmType 이 없으면 CENTRIC
    plmType: isPlmType(attributes.plmType) ? attributes.plmType : 'CENTRIC',
    styleNumber: nonEmpty(attributes.styleNumber) ?? '',
    content,
  };
};

export const parseProductLifecycleContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => nonEmpty(attributes?.content);

export const buildProductLifecycleAttributes = (
  state: ProductLifecycleState | null | undefined,
) =>
  state
    ? {
        plmType: state.plmType,
        styleNumber: state.styleNumber,
        content: state.content,
      }
    : null;

export const checkProductLifecycleBeforeSave = (
  state: ProductLifecycleState | null | undefined,
): string | null =>
  state?.content && state.styleNumber
    ? null
    : PRODUCT_LIFECYCLE_MISSING_MESSAGE;

export const productLifecycleKey = (
  state: ProductLifecycleState | null | undefined,
) => (state ? `${state.plmType}:${state.styleNumber}` : '');
